/**
 * Chunked RPC - send one large RPC argument over WebSocket in small pieces.
 */

import { RPC_CHANNELS } from '@okclawpro/shared/protocol'
import type { RpcClient } from '@okclawpro/server-core/transport'

export const CHUNK_SIZE = 2 * 1024 * 1024
export const CHUNKED_TRANSFER_THRESHOLD = 5 * 1024 * 1024

const MAX_CHUNK_RETRIES = 3
const CHUNK_RETRY_DELAY = 1000

export interface PreparedChunkedPayload {
  bytes: Uint8Array
  checksum: string
  chunkCount: number
}

export interface ChunkedProgress {
  sent: number
  total: number
  bytesSent: number
  totalBytes: number
}

export interface ChunkedInvokeOptions {
  timeoutMs?: number
}

interface RpcClientWithInvokeOptions extends Pick<RpcClient, 'invoke'> {
  invokeWithOptions?: (channel: string, options: ChunkedInvokeOptions | undefined, ...args: any[]) => Promise<any>
}

export function getChunkCount(totalBytes: number): number {
  return Math.ceil(totalBytes / CHUNK_SIZE)
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digestInput = new Uint8Array(bytes).buffer
  const digest = await globalThis.crypto.subtle.digest('SHA-256', digestInput)
  return bytesToHex(new Uint8Array(digest))
}

function bytesToBase64(bytes: Uint8Array): string {
  const nodeBuffer = (globalThis as { Buffer?: { from(value: Uint8Array): { toString(encoding: 'base64'): string } } }).Buffer
  if (nodeBuffer) {
    return nodeBuffer.from(bytes).toString('base64')
  }

  let binary = ''
  const batchSize = 0x8000
  for (let i = 0; i < bytes.length; i += batchSize) {
    const chunk = bytes.subarray(i, Math.min(i + batchSize, bytes.length))
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

export async function prepareChunkedPayload(value: unknown): Promise<PreparedChunkedPayload> {
  const json = JSON.stringify(value)
  const bytes = new TextEncoder().encode(json)
  return {
    bytes,
    checksum: await sha256Hex(bytes),
    chunkCount: getChunkCount(bytes.length),
  }
}

export async function invokeChunked(
  client: RpcClientWithInvokeOptions,
  channel: string,
  args: any[],
  largeArgIndex: number,
  onProgress?: (progress: ChunkedProgress) => void,
  prepared?: PreparedChunkedPayload,
  options?: ChunkedInvokeOptions,
): Promise<any> {
  const payload = prepared ?? await prepareChunkedPayload(args[largeArgIndex])
  const invoke = (targetChannel: string, ...targetArgs: any[]) => {
    if (options?.timeoutMs != null && typeof client.invokeWithOptions === 'function') {
      return client.invokeWithOptions(targetChannel, options, ...targetArgs)
    }
    return client.invoke(targetChannel, ...targetArgs)
  }

  const deferredArgs = [...args]
  deferredArgs[largeArgIndex] = null

  let transferId: string | null = null
  try {
    const startResult = await invoke(RPC_CHANNELS.transfer.START, {
      totalBytes: payload.bytes.length,
      chunkCount: payload.chunkCount,
      channel,
      args: deferredArgs,
      largeArgIndex,
      checksum: payload.checksum,
    }) as { transferId: string }

    transferId = startResult.transferId

    for (let i = 0; i < payload.chunkCount; i++) {
      const start = i * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, payload.bytes.length)
      const data = bytesToBase64(payload.bytes.subarray(start, end))

      let lastError: Error | null = null
      for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
        try {
          await invoke(RPC_CHANNELS.transfer.CHUNK, {
            transferId,
            index: i,
            data,
          })
          lastError = null
          break
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err))
          if (attempt < MAX_CHUNK_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, CHUNK_RETRY_DELAY))
          }
        }
      }

      if (lastError) {
        throw new Error(`Chunk ${i + 1}/${payload.chunkCount} failed after ${MAX_CHUNK_RETRIES} attempts: ${lastError.message}`)
      }

      onProgress?.({
        sent: i + 1,
        total: payload.chunkCount,
        bytesSent: end,
        totalBytes: payload.bytes.length,
      })
    }

    const result = await invoke(RPC_CHANNELS.transfer.COMMIT, { transferId })
    transferId = null
    return result
  } catch (error) {
    if (transferId) {
      try {
        await invoke(RPC_CHANNELS.transfer.ABORT, { transferId })
      } catch {
        // Best-effort cleanup only.
      }
    }
    throw error
  }
}
